import { useState } from 'react';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { NavIcon } from '@l2u/ui/components/NavIcon.tsx';
import { adminApi } from '@l2u/ui/lib/api.ts';
import { questionnaireProgress } from '../lib/companyDetailProgress.ts';
import type { QuestionnaireGroup } from '../lib/companyDetailTabs.ts';
import {
  buildPdfSections,
  buildQuestionnairePdfDoc,
  pdfFileName,
} from '../lib/questionnairePdf.ts';
import type { AdminSubmission, SubmissionComment } from '@l2u/ui/lib/types.ts';
import type { CompletionReason } from '@l2u/ui/lib/visibility.ts';

type Props = {
  companyId: string;
  companyName: string;
  vendorNumber: string | null;
  group: QuestionnaireGroup;
  subByQ: ReadonlyMap<string, AdminSubmission>;
  visibleIds: Set<string>;
  completionByQuestion: Map<string, CompletionReason | null>;
};

// Fetch comment threads only for submissions that actually have comments
// (latest_comment_author != null). Best-effort: a failed thread is recorded in
// `unavailable` and the export still succeeds.
async function fetchComments(
  companyId: string,
  submissions: AdminSubmission[],
): Promise<{
  bySubmission: Map<string, SubmissionComment[]>;
  unavailable: Set<string>;
}> {
  const withComments = submissions.filter((s) => s.latest_comment_author != null);
  const bySubmission = new Map<string, SubmissionComment[]>();
  const unavailable = new Set<string>();
  const results = await Promise.allSettled(
    withComments.map((s) =>
      adminApi.get<{ comments: SubmissionComment[] }>(
        `/api/companies/${companyId}/submissions/${s.id}/comments`,
      ),
    ),
  );
  results.forEach((res, i) => {
    const sid = withComments[i]!.id;
    if (res.status === 'fulfilled') bySubmission.set(sid, res.value.comments);
    else unavailable.add(sid);
  });
  return { bySubmission, unavailable };
}

// Lazy-load pdfmake (engine + ~1MB Roboto vfs) only on first export so it stays
// out of the main bundle. The vfs export shape varies across pdfmake versions:
// 0.3.x does `module.exports = vfs`, so the default export IS the font map
// (keys like "Roboto-Regular.ttf"); older builds nest it under .vfs /
// .pdfMake.vfs. Pick the first candidate that actually looks like the font map
// (a bare `??` chain silently resolves to undefined on the wrong shape, and
// pdfmake then throws "Roboto-Medium.ttf not found").
async function downloadPdf(
  doc: TDocumentDefinitions,
  filename: string,
): Promise<void> {
  const [pdfMakeModule, pdfFontsModule] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const pdfMake: any = (pdfMakeModule as any).default ?? pdfMakeModule;
  const fonts: any = pdfFontsModule as any;
  const isVfs = (o: any): boolean =>
    !!(o && typeof o === 'object' && o['Roboto-Regular.ttf']);
  const vfs =
    (isVfs(fonts.vfs) && fonts.vfs) ||
    (isVfs(fonts.pdfMake?.vfs) && fonts.pdfMake.vfs) ||
    (isVfs(fonts.default?.pdfMake?.vfs) && fonts.default.pdfMake.vfs) ||
    (isVfs(fonts.default?.vfs) && fonts.default.vfs) ||
    (isVfs(fonts.default) && fonts.default) ||
    (isVfs(fonts) && fonts) ||
    undefined;
  // pdfmake 0.3.x registers fonts via addVirtualFileSystem; older versions read
  // the `vfs` property. Use whichever this version provides.
  if (typeof pdfMake.addVirtualFileSystem === 'function' && vfs) {
    pdfMake.addVirtualFileSystem(vfs);
  } else {
    pdfMake.vfs = vfs;
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */
  pdfMake.createPdf(doc).download(filename);
}

export function ExportQuestionnaireButton({
  companyId,
  companyName,
  vendorNumber,
  group,
  subByQ,
  visibleIds,
  completionByQuestion,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onExport() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const submissions = group.sections
        .flatMap((s) => s.questions)
        .map((q) => subByQ.get(q.id))
        .filter((s): s is AdminSubmission => s !== undefined);
      const { bySubmission, unavailable } = await fetchComments(companyId, submissions);
      const sections = buildPdfSections(group, subByQ, bySubmission, unavailable);
      const progress = questionnaireProgress(group, visibleIds, subByQ, completionByQuestion);
      const generatedAt = new Date().toISOString();
      const doc = buildQuestionnairePdfDoc({
        companyName,
        vendorNumber,
        questionnaireName: group.questionnaire.name,
        progress,
        generatedAt,
        sections,
      });
      await downloadPdf(doc, pdfFileName(companyName, group.questionnaire.name, generatedAt));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Btn size="sm" variant="info" onClick={() => void onExport()} loading={busy}>
        <NavIcon name="file-text" size={16} />
        Export PDF
      </Btn>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}
